import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AuthSession, UserProfile } from '@/types/auth';

function resolveDeviceId(): string {
  const key = 'vynzo_admin_device_id';
  let id = localStorage.getItem(key);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(key, id);
  }
  return id;
}

interface AuthState {
  user: UserProfile | null;
  accessToken: string | null;
  refreshToken: string | null;
  readonly deviceId: string;
  setSession: (session: AuthSession) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  updateUser: (user: UserProfile) => void;
  clearSession: () => void;
  isAuthenticated: () => boolean;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasRole: (role: string) => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      deviceId: resolveDeviceId(),

      setSession: (session) =>
        set({
          user: session.user,
          accessToken: session.access_token,
          refreshToken: session.refresh_token,
        }),

      setTokens: (accessToken, refreshToken) => set({ accessToken, refreshToken }),

      updateUser: (user) => set({ user }),

      clearSession: () => set({ user: null, accessToken: null, refreshToken: null }),

      isAuthenticated: () => get().accessToken !== null && get().user !== null,

      hasPermission: (permission) => get().user?.permissions.includes(permission) ?? false,

      hasAnyPermission: (permissions) => {
        const userPermissions = get().user?.permissions ?? [];
        return permissions.some((p) => userPermissions.includes(p));
      },

      hasRole: (role) => get().user?.roles.includes(role) ?? false,
    }),
    {
      name: 'vynzo-admin-auth',
      partialize: (state) => ({
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
      }),
    },
  ),
);
